import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
  memo,
} from "react";
import "./CSS/SvgAdminPanel.css";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import ButtonGroup from "@mui/material/ButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import axios from "axios";
import { parse } from "jsonc-parser";
import { DataGrid } from "@mui/x-data-grid";
import { Tabs, Tab } from "@mui/material";
import { ExpandMore, ChevronRight } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import PhotoIcon from "@mui/icons-material/Photo";
import { Toast } from "primereact/toast";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import DeleteIcon from "@mui/icons-material/Delete";
import { alignProperty } from "@mui/material/styles/cssUtils";
import { Tree } from "primereact/tree";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import LinearProgress from "@mui/material/LinearProgress";
import FolderManagementPanel from "./SvgAdminPanelPanels/FolderManagementPanel";
import ShopAuthManagement from "./SvgAdminPanelPanels/ShopAuthManagement";
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useUser } from "./Context/UserContext";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Geliştirilmiş debounce helper
function debounce(fn, delay) {
  let t;
  const wrapped = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
  wrapped.cancel = () => clearTimeout(t);
  wrapped.flush = () => {
    clearTimeout(t);
    fn();
  };
  return wrapped;
}

/* ---------------------------------------------------
   Context + RecursiveAccordion component - Memoized
--------------------------------------------------- */
export const PathContext = React.createContext([]);

// RecursiveAccordion: Memoized ve optimized
const RecursiveAccordion = memo(
  function RecursiveAccordion({ data, parentPath = [], onSelect }) {
    const [hoveredItem, setHoveredItem] = useState(null);
    const [expanded, setExpanded] = useState({});
    const currentPath = useContext(PathContext);

    const newPath = data?.name ? [...currentPath, data.name] : currentPath;
    const fullPath = newPath.join(".");

    useEffect(() => {
      if (data?.autoOpen) {
        setExpanded((prev) => ({ ...prev, [fullPath]: true }));
      }
    }, [fullPath, data?.autoOpen]);

    const handleChange = useCallback(
      (_, isExpanded) => {
        setExpanded((prev) => ({ ...prev, [fullPath]: isExpanded }));
      },
      [fullPath]
    );

    const changeImageUrl = useCallback(
      (detail) => {
        onSelect(newPath, detail);
      },
      [newPath, onSelect]
    );

    if (!data) return null;

    return (
      <PathContext.Provider value={newPath}>
        <Accordion
          expanded={!!expanded[fullPath]}
          onChange={handleChange}
          style={{
            background: data.background,
            borderRadius: data.borderRadius,
            overflow: "hidden",
            position: "relative",
          }}
          onMouseEnter={() => setHoveredItem(data.name)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <AccordionSummary
            expandIcon={<ArrowDownwardIcon />}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography>{data.name}</Typography>
            {data.url && (
              <img
                src={
                  hoveredItem === data.name && data.hoverImageUrl
                    ? data.hoverImageUrl
                    : data.url
                }
                alt="icon"
                style={{ width: 50, height: 50 }}
              />
            )}
          </AccordionSummary>

          <AccordionDetails
            style={{
              maxHeight: data.maxHeight,
              overflow: "auto",
              background: data.background,
            }}
          >
            {Array.isArray(data.details) &&
              data.details.map((detail, index) => {
                // ============================================
                // UNIQUE KEY OLUŞTURMA - DÜZELTME
                // ============================================
                // Her detail için benzersiz key oluştur
                // 1. Öncelik: detail.id varsa kullan
                // 2. Yoksa: fullPath + index kombinasyonu kullan
                const uniqueId = detail.id || `${fullPath}::item-${index}`;
                
                // Alternatif: buttonName varsa onu da ekle
                const itemKey = detail.id 
                  ? detail.id 
                  : `${fullPath}::${detail.name || detail.buttonName || 'item'}-${index}`;

                // ============================================
                // RENDER LOGIC
                // ============================================
                if (detail.name === "SubShop1Button") {
                  return (
                    <div key={itemKey}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "10px",
                        }}
                      >
                        <Button
                          onClick={() => changeImageUrl(detail)}
                          variant="contained"
                          style={{
                            backgroundColor: "#f5f5dc",
                            color: "#000",
                            display: "flex",
                            alignItems: "center",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            textTransform: "none",
                            width: "-webkit-fill-available",
                          }}
                        >
                          {detail.url && !detail.url.includes("EmptySvg") && (
                            <img
                              src={detail.url}
                              alt="icon"
                              style={{
                                width: 50,
                                height: 50,
                                marginRight: "8px",
                                borderRadius: "4px",
                              }}
                            />
                          )}
                          {detail.buttonName}
                        </Button>
                      </div>
                    </div>
                  );
                } else if (detail.subShops) {
                  return (
                    <RecursiveAccordion
                      key={itemKey}
                      data={{
                        name: detail.name,
                        details: detail.subShops,
                        maxHeight: detail.maxHeight,
                        autoOpen: detail.autoOpen,
                        buttonText: detail.buttonText,
                        background: detail.background,
                        url: detail.url,
                        hoverImageUrl: detail.hoverImageUrl,
                      }}
                      onSelect={onSelect}
                    />
                  );
                } else {
                  return (
                    <div key={itemKey}>
                      <div>{detail.name}</div>
                    </div>
                  );
                }
              })}
          </AccordionDetails>
        </Accordion>
      </PathContext.Provider>
    );
  },
  (prev, next) => prev.data === next.data
);


// SVG Preview Component - Ayrı bir component olarak
const SvgPreview = memo(({ svgContent, svgExample }) => {
  const displayContent = svgExample || svgContent;

  if (!displayContent) {
    return (
      <div>
        <p>Lütfen Düzenlemek İstediğiniz Dizaynı Seçiniz.</p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "10px",
        boxSizing: "border-box",
      }}
    >
      <iframe
        srcDoc={`
          <html>
            <head>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                html, body { height: 100%; overflow: hidden; }
                body { 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  padding: 10px;
                }
                svg { 
                  max-width: 100%; 
                  max-height: 100%; 
                  width: auto; 
                  height: auto; 
                  display: block;
                }
              </style>
            </head>
            <body>${displayContent}</body>
          </html>
        `}
        style={{
          width: "100%",
          height: "100%",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
        title="SVG Preview"
      />
    </div>
  );
});

const customTreeStyles = `
  /* Resim dosyaları için özel stil */
  .image-node {
    background-color: #e3f2fd !important;
    border-left: 3px solid #2196f3 !important;
    border-radius: 6px !important;
    margin: 2px 0 !important;
  }
  
  .image-node:hover {
    background-color: #bbdefb !important;
  }
  
  /* Folder ikonları için */
  .p-tree .p-treenode-content .pi-folder {
    color: #ffa726 !important;
    font-size: 18px !important;
  }
  
  /* Resim ikonları için */
  .p-tree .p-treenode-content .pi-image {
    color: #42a5f5 !important;
    font-size: 18px !important;
  }
  
  /* Dosya ikonları için */
  .p-tree .p-treenode-content .pi-file {
    color: #78909c !important;
    font-size: 16px !important;
  }
  
  /* Seçili öğeler için */
  .p-tree .p-treenode-content.p-highlight.image-node {
    background-color: #1976d2 !important;
    color: white !important;
  }
  
  /* Tree node hover efekti */
  .p-tree .p-treenode-content:hover {
    background-color: #f5f5f5 !important;
  }
  
  .p-tree .p-treenode-content.image-node:hover {
    background-color: #bbdefb !important;
  }
`;

const SvgAdminPanel = () => {
  let status = "1";
  let path =
    status === "0" ? "http://localhost:3003" : "https://outlettee.com:3003";
const { user } = useUser();
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = customTreeStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Batch state'ler için objects kullanma
  const [textInputs, setTextInputs] = useState({
    text: "",
    text1: "",
    text2: "",
    text3: "",
    textArea: "",
  });

  const [rectangleStates, setRectangleStates] = useState({
    rectangleX: "",
    rectangleY: "",
    rectangleWidth: "",
    rectangleHeight: "",
    rectangleX2: "",
    rectangleY2: "",
    rectangleWidth2: "",
    rectangleHeight2: "",
    rectangleX3: "",
    rectangleY3: "",
    rectangleWidth3: "",
    rectangleHeight3: "",
    rectangleX4: "",
    rectangleY4: "",
    rectangleWidth4: "",
    rectangleHeight4: "",
  });

  const [textHeights, setTextHeights] = useState({
    textHeight1: "",
    textHeight2: "",
    textHeight3: "",
    textHeight4: "",
  });

  const [fontFamilies, setFontFamilies] = useState({
    fontFamily: "",
    fontFamily2: "",
    fontFamily3: "",
    fontFamily4: "",
  });

  const [textCurves, setTextCurves] = useState({
    textCurve1: "",
    textCurve2: "",
    textCurve3: "",
    textCurve4: "",
  });

  const [rotationAngles, setRotationAngles] = useState({
    rotationAngle1: 0,
    rotationAngle2: 0,
    rotationAngle3: 0,
    rotationAngle4: 0,
  });

  const [visibility, setVisibility] = useState({
    text1Visible: false,
    text2Visible: false,
    text3Visible: false,
    text4Visible: false,
    textAreaVisible: false,
  });

  // Diğer state'ler
  const [imageUrl, setImageUrl] = useState("");
  const [selectedAligment, setselectedAligment] = useState("left");
  const [fontStatus, setFontStatus] = useState("false");
  const [designType, setDesignType] = useState("");
  const [lineCounts, setLineCounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState("processed-image.svg");
  const [lineHeight, setLineHeight] = useState("55");
  const [shopData, setShopData] = useState([]);
  const shopDataRef = useRef([]);
  const [presentPath, setPresentPath] = useState("");
  const [rows, setRows] = useState([]);
  const [parentID, setParentID] = useState("");
  const [childID, setChildID] = useState("");
  const [nodes, setNodes] = useState([]);
  const [checked, setChecked] = useState({});
  const [expanded, setExpanded] = useState([]);
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const [showFiles, setShowFiles] = useState(1);
  const [dimensions, setDimensions] = useState({ width: "", height: "" });
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [fontList, setFontList] = useState([]);
  const [svgContent, setSvgContent] = useState("");
  const [svgExample, setSvgExample] = useState("");
  const [value, setValue] = useState(0);
  const [checkedRows, setCheckedRows] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [filledRows, setFilledRows] = useState([]);
  const [rowsAdd, setRowsAdd] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [updatedRects, setUpdatedRects] = useState(null);
  const [updateKey, setUpdateKey] = useState(0);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [selectedKeys, setSelectedKeys] = useState({});
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [fontFiles, setFontFiles] = useState([]); // { file, error } dizisi
  const [fontUploading, setFontUploading] = useState(false);
  const [fontProgress, setFontProgress] = useState({}); // { fileName: 0-100 }
  const treeContainerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  let imagePathRoot = status === "0" ? "ImagesTest/Shops/" : "Images/Shops/";

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const CustomButton = styled(Button)({
    borderColor: "black",
    color: "black",
    border: "2px solid #1A1A1A",
    borderRadius: "15px",
    boxSizing: "border-box",
    fontFamily:
      'Roobert,-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
    fontSize: "12px",
    fontWeight: 500,
    minHeight: "20px",
    padding: "16px 24px",
    transition: "all 300ms cubic-bezier(.23, 1, 0.32, 1)",
    width: "20%",
    marginBottom: "6%",
    "&:hover": {
      color: "#fff",
      backgroundColor: "#1A1A1A",
      borderColor: "rgba(121, 147, 81)",
      boxShadow: "rgba(0, 0, 0, 0.25) 0 8px 15px",
      transform: "translateY(-2px)",
    },
    "&:active": {
      boxShadow: "none",
      transform: "translateY(0)",
    },
    "&:disabled": {
      pointerEvents: "none",
    },
  });

  // Fetch shop data
 useEffect(() => {
  let mounted = true;
  
  const fetchShopData = async () => {
    if (!user?.ID) {
      console.error("User ID not found");
      return;
    }

    try {
      const response = await axios.post(path + "/api/shopDataFiltered", {
        UserID: user.ID,
        Admin: user.Admin || 0,
        DesignAdmin: user.DesignAdmin || 0
      });
      console.log("Shop data fetched:", response.data);
      if (!mounted) return;

      setShopData(response.data);
      shopDataRef.current = response.data;
      
    } catch (error) {
      console.error("Error fetching shop data:", error);
      if (mounted) {
        setShopData([]);
        shopDataRef.current = [];
      }
    }
  };

  fetchShopData();

  return () => {
    mounted = false;
  };
}, [user])

  // Fetch font list - optimized
  useEffect(() => {
    let mounted = true;
    const fetchFontList = async () => {
      try {
        const response = await axios.get(path + "/api/fontList");
        if (!mounted) return;
        setFontList(response.data);
      } catch (error) {
        console.error("Error fetching font list:", error);
      }
    };
    fetchFontList();
    return () => {
      mounted = false;
    };
  }, [path]);

  // Fetch folders - optimized
  useEffect(() => {
  const fetchFolders = async () => {
    const response = await axios.post(path + "/api/foldersFiltered", {
      UserID: user.ID,
      Admin: user.Admin || 0,
      DesignAdmin: user.DesignAdmin || 0,
      showFiles: 1
    });
    setNodes(processData(response.data));
  };
  fetchFolders();
}, [user, refetchTrigger, path]);

  // SVG content fetcher - optimized
  useEffect(() => {
    setSvgExample("");
    if (!imageUrl) {
      setSvgContent("");
      return;
    }
    let mounted = true;
    fetch(imageUrl)
      .then((response) => response.text())
      .then((data) => {
        if (mounted) setSvgContent(data);
      })
      .catch(() => {
        if (mounted) setSvgContent("");
      });
    return () => {
      mounted = false;
    };
  }, [imageUrl]);

  // Memoized rects calculation
  const rects = useMemo(
    () => [
      {
        x: rectangleStates.rectangleX,
        y: rectangleStates.rectangleY,
        width: rectangleStates.rectangleWidth,
        height: rectangleStates.rectangleHeight,
        text1: textInputs.text || textInputs.textArea,
        textHeight1: textHeights.textHeight1,
        fontFamily: fontFamilies.fontFamily,
        textCurve: textCurves.textCurve1,
        rotationAngle: rotationAngles.rotationAngle1,
      },
      {
        x: rectangleStates.rectangleX2,
        y: rectangleStates.rectangleY2,
        width: rectangleStates.rectangleWidth2,
        height: rectangleStates.rectangleHeight2,
        text2: textInputs.text1,
        textHeight2: textHeights.textHeight2,
        fontFamily: fontFamilies.fontFamily2 || fontFamilies.fontFamily,
        textCurve: textCurves.textCurve2,
        rotationAngle: rotationAngles.rotationAngle2,
      },
      {
        x: rectangleStates.rectangleX3,
        y: rectangleStates.rectangleY3,
        width: rectangleStates.rectangleWidth3,
        height: rectangleStates.rectangleHeight3,
        text3: textInputs.text2,
        textHeight3: textHeights.textHeight3,
        fontFamily: fontFamilies.fontFamily3 || fontFamilies.fontFamily,
        textCurve: textCurves.textCurve3,
        rotationAngle: rotationAngles.rotationAngle3,
      },
      {
        x: rectangleStates.rectangleX4,
        y: rectangleStates.rectangleY4,
        width: rectangleStates.rectangleWidth4,
        height: rectangleStates.rectangleHeight4,
        text4: textInputs.text3,
        textHeight4: textHeights.textHeight4,
        fontFamily: fontFamilies.fontFamily4 || fontFamilies.fontFamily,
        textCurve: textCurves.textCurve4,
        rotationAngle: rotationAngles.rotationAngle4,
      },
    ],
    [
      rectangleStates,
      textInputs,
      textHeights,
      fontFamilies,
      textCurves,
      rotationAngles,
    ]
  );

  // Handle close
  const handleClose = () => setOpen(false);

  // Tab change handler
  const handleTabChange = useCallback((event, newValue) => {
    setValue(newValue);
    setShowFiles(newValue === 0 ? 1 : 0);
  }, []);

  const handleChanges = useCallback((event, newValue) => {
    setValue(newValue);
    if (newValue === 0) {
      setShowFiles(1);
      setSelectedPaths([]);
      setImageUrl("");
    } else if (newValue === 1) {
      setShowFiles(0);
      setSelectedPaths([]);
      setImageUrl("");
    }
  }, []);

  // Process data function
  const processData = useCallback((data) => {
    return data.map((item) => ({
      id: item.key,
      label: item.label,
      children: item.children ? processData(item.children) : [],
    }));
  }, []);

  // Download SVG function
  const downloadSVG = useCallback(async () => {
    try {
      const processedSVG = await processSVG(false);
      const blob = await processedSVG.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      handleClose();
    } catch (error) {
      console.error("Error:", error);
    }
  }, [fileName]);

  // Process SVG function with optimizations
  const processSVG = useCallback(
    async (test, updatedRectsParam) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(
        svgContent || "<svg />",
        "image/svg+xml"
      );
      const textElement = doc.createElementNS(
        "http://www.w3.org/2000/svg",
        "text"
      );
      textElement.textContent = textInputs.text;
      doc.documentElement.appendChild(textElement);
      const serializedSVG = new XMLSerializer().serializeToString(doc);

      return fetch(path + "/process-svg", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: textInputs.text,
          svg: serializedSVG,
          rects: updatedRectsParam || rects,
          fontFamily: fontFamilies.fontFamily,
          fontStatus: fontStatus,
          alignment: selectedAligment,
          designType: designType,
          test: test,
          lineHeight: lineHeight,
          rotationAngle: rotationAngles.rotationAngle1,
          dimensions: dimensions,
          adminPanel: true,
        }),
      });
    },
    [
      svgContent,
      textInputs.text,
      rects,
      fontFamilies.fontFamily,
      fontStatus,
      selectedAligment,
      designType,
      lineHeight,
      rotationAngles.rotationAngle1,
      dimensions,
      path,
    ]
  );

  // Try SVG with debouncing
  const trySVGRaw = useCallback(
    async (updatedRectsParam) => {
      try {
        setSvgExample("");
        const processedSVG = await processSVG(true, updatedRectsParam);
        const data = await processedSVG.text();
        setSvgExample(data);
      } catch (error) {
        console.error("Error fetching SVG:", error);
      }
    },
    [processSVG]
  );

  const trySVGDebounced = useMemo(() => debounce(trySVGRaw, 500), [trySVGRaw]);

  // OnSelect callback for RecursiveAccordion - optimized
  const onAccordionSelect = useCallback((newPath, detail) => {
    setFilledRows([]);
    setRowsAdd([]);
    setSelectedRows([]);
    setCheckedRows({});
    setUpdatedRects(null);
    setSvgExample("");

    console.log("Selected path:", detail);
    setTextInputs({
      text: "",
      text1: "",
      text2: "",
      text3: "",
      textArea: "",
    });

    setImageUrl(detail.url || "");

    setRectangleStates({
      rectangleX: detail.x1 || "",
      rectangleY: detail.y1 || "",
      rectangleWidth: detail.width1 || "",
      rectangleHeight: detail.height1 || "",
      rectangleX2: detail.x2 || "",
      rectangleY2: detail.y2 || "",
      rectangleWidth2: detail.width2 || "",
      rectangleHeight2: detail.height2 || "",
      rectangleX3: detail.x3 || "",
      rectangleY3: detail.y3 || "",
      rectangleWidth3: detail.width3 || "",
      rectangleHeight3: detail.height3 || "",
      rectangleX4: detail.x4 || "",
      rectangleY4: detail.y4 || "",
      rectangleWidth4: detail.width4 || "",
      rectangleHeight4: detail.height4 || "",
    });

    setTextHeights({
      textHeight1: detail.textHeight1 || "",
      textHeight2: detail.textHeight2 || "",
      textHeight3: detail.textHeight3 || "",
      textHeight4: detail.textHeight4 || "",
    });

    setFontFamilies({
      fontFamily: detail.fontFamily || "",
      fontFamily2: detail.fontFamily2 || "",
      fontFamily3: detail.fontFamily3 || "",
      fontFamily4: detail.fontFamily4 || "",
    });

    setVisibility({
      text1Visible: !!detail.text1Visible,
      text2Visible: !!detail.text2Visible,
      text3Visible: !!detail.text3Visible,
      text4Visible: !!detail.text4Visible,
      textAreaVisible: !!detail.textAreaVisible,
    });

    setTextCurves({
      textCurve1: detail.textCurve1 || "",
      textCurve2: detail.textCurve2 || "",
      textCurve3: detail.textCurve3 || "",
      textCurve4: detail.textCurve4 || "",
    });

    setRotationAngles({
      rotationAngle1: detail.rotationAngle1 || 0,
      rotationAngle2: detail.rotationAngle2 || 0,
      rotationAngle3: detail.rotationAngle3 || 0,
      rotationAngle4: detail.rotationAngle4 || 0,
    });

    setDesignType(detail.designType || "");
    setChildID(detail.id);
    setPresentPath(newPath.join(" > "));
  }, []);

  // Memoized columns for DataGrid
  const responseColumns = useMemo(
    () => [
      {
        field: "selection",
        headerName: "",
        width: 50,
        sortable: false,
        renderCell: (params) => {
          return (
            <Checkbox
              checked={selectedRows.includes(params.row.id)}
              onChange={(event) => {
                const isChecked = event.target.checked;
                setSelectedRows((prev) => (isChecked ? [params.row.id] : []));
                setCheckedRows((prev) => {
                  const newChecked = {};
                  if (isChecked) {
                    newChecked[params.row.id] = true;
                  }
                  return newChecked;
                });
              }}
            />
          );
        },
      },
      {
        field: "Height",
        headerName: "Height",
        width: 60,
        editable: true,
        type: "number",
      },
      {
        field: "Width",
        headerName: "Width",
        width: 60,
        editable: true,
        type: "number",
      },
      {
        field: "X",
        headerName: "X",
        width: 50,
        editable: true,
        type: "number",
      },
      {
        field: "Y",
        headerName: "Y",
        width: 50,
        editable: true,
        type: "number",
      },
      {
        field: "TextHeight",
        headerName: "Text Height",
        width: 80,
        editable: true,
        type: "number",
      },
      {
        field: "Font",
        headerName: "Font",
        width: 130,
        editable: true,
        type: "singleSelect",
        valueOptions: fontList.map((font) => font.name),
      },
      {
        field: "Rotation",
        headerName: "Rotation",
        width: 75,
        editable: true,
        type: "number",
      },
      {
        field: "Curve",
        headerName: "Curve",
        width: 75,
        editable: true,
        type: "number",
      },
      { field: "Text", headerName: "Deneme Text", width: 75, editable: true },
      {
        field: "actions",
        headerName: "Sil",
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const onClick = (e) => {
            e.stopPropagation();
            handleDeleteRow(params.id);
          };
          return (
            <IconButton onClick={onClick}>
              <DeleteIcon color="error" />
            </IconButton>
          );
        },
      },
    ],
    [fontList, selectedRows]
  );

  // Fill table function - optimized
  const fillTable = useCallback(() => {
    const filledRects = rects.filter(
      (rect) => rect && rect.height != null && rect.height !== ""
    );

    const timestamp = Date.now();
    const dummy = filledRects
      .filter((rect) => rect.height !== 0 && rect.width !== 0)
      .map((rect, index) => {
        if (!rect) return null;

        return {
          id: `filled_${timestamp}_${index}`,
          Height: rect.height || 0,
          Width: rect.width || 0,
          X: rect.x || 0,
          Y: rect.y || 0,
          TextHeight: rect.textHeight1 || 0,
          Font: rect.fontFamily || "",
          Rotation: rect.rotationAngle || 0,
          Curve: rect.textCurve || 0,
          Text: "",
        };
      });

    setFilledRows((prevRows) => {
      const existingIds = new Set(prevRows.map((row) => row.id));
      const newRows = dummy.filter((row) => !existingIds.has(row.id));
      return [...prevRows, ...newRows];
    });
  }, [rects]);

  // Add empty row function
  const addEmptyRow = useCallback(() => {
    const newRow = {
      id: `new_${Date.now()}`,
      Height: 0,
      Width: 0,
      X: 0,
      Y: 0,
      TextHeight: 0,
      Font: "Times.ttf",
      Rotation: 0,
      Curve: 0,
      Text: "",
    };
    setRowsAdd((prev) => [...prev, newRow]);
  }, []);

  // Delete row function
  const handleDeleteRow = useCallback((id) => {
    console.log("Deleting row with id:", id);
    setRowsAdd((prevRows) => prevRows.filter((row) => row.id !== id));
  }, []);

  // Process all rows function - optimized
  const processAllRows = useCallback(() => {
    try {
      const allRows = [
        ...new Map(
          [...filledRows, ...rowsAdd].map((row) => [row.id, row])
        ).values(),
      ];

      const newRects = allRows.map((row, index) => ({
        x: row.X,
        y: row.Y,
        width: row.Width,
        height: row.Height,
        [`text${index + 1}`]: row.Text,
        textHeight: row.TextHeight,
        fontFamily: row.Font || "defaultFont",
        textCurve: row.Curve,
        rotationAngle: row.Rotation,
      }));

      setUpdatedRects(newRects);
      setUpdateKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error in processAllRows:", error);
    }
  }, [filledRows, rowsAdd]);

  // UseEffect for updated rects
  useEffect(() => {
    if (updatedRects) {
      trySVGDebounced(updatedRects);
    }
  }, [updatedRects, updateKey, trySVGDebounced]);

  // UseEffect for filling table
  useEffect(() => {
    if (rects.length > 0) {
      setFilledRows((prevRows) => {
        if (prevRows.length === 0) {
          fillTable();
        }
        return prevRows;
      });
    }
  }, [
    rectangleStates.rectangleX,
    rectangleStates.rectangleX2,
    rectangleStates.rectangleX3,
    rectangleStates.rectangleX4,
    fillTable,
  ]);

  // Button click handler - optimized
  const handleButtonClick = useCallback(
    (event) => {
      const buttonId = event.currentTarget.id;
      const step = 5;

      if (selectedRows.length === 0) {
        console.log("No row selected");
        return;
      }

      const selectedRowId = selectedRows[0];
      let updatedRow;

      const updatePosition = (row) => {
        let newX = row.X;
        let newY = row.Y;

        switch (buttonId) {
          case "up":
            newY -= step;
            break;
          case "down":
            newY += step;
            break;
          case "left":
            newX -= step;
            break;
          case "right":
            newX += step;
            break;
        }

        return { ...row, X: newX, Y: newY };
      };

      // Check if the selected row is in filledRows
      if (filledRows.find((row) => row.id === selectedRowId)) {
        setFilledRows((prevRows) =>
          prevRows.map((row) => {
            if (row.id === selectedRowId) {
              updatedRow = updatePosition(row);
              return updatedRow;
            }
            return row;
          })
        );
      } else if (rowsAdd.find((row) => row.id === selectedRowId)) {
        setRowsAdd((prevRows) =>
          prevRows.map((row) => {
            if (row.id === selectedRowId) {
              updatedRow = updatePosition(row);
              return updatedRow;
            }
            return row;
          })
        );
      }

      processAllRows();
    },
    [selectedRows, filledRows, rowsAdd, processAllRows]
  );

  // File change handler
  const handleFileChange = useCallback((event) => {
    event.preventDefault();
    const files = event.target.files;
    if (files && files.length > 0) {
      setUploadedFile(files[0]);
    }
  }, []);

  // Remove file handler
  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null);
  }, []);

  // File upload handler
  const handleFileUpload = useCallback(async () => {
    if (!uploadedFile) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Lütfen bir dosya seçin",
      });
      return;
    }

    const formData = new FormData();
    formData.append("folderPath", selectedPaths[selectedPaths.length - 1]);
    formData.append("file", uploadedFile);

    try {
      const response = await fetch(path + "/api/UploadImage", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Dosya başarıyla yüklendi",
        });
        console.log("File uploaded successfully");
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Dosya yükleme başarısız oldu",
        });
        console.error("File upload failed");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Dosya yüklenirken bir hata oluştu: " + error.message,
      });
      console.error("Error uploading file:", error);
    }
  }, [uploadedFile, selectedPaths, path]);

  // Folder submit handler
  const handleFolderSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const folderName = event.target.folderName.value;

      if (!folderName.trim()) {
        return;
      }

      const formData = new FormData();
      formData.append("folderName", folderName);
      formData.append("folderPath", selectedPaths[selectedPaths.length - 1]);

      try {
        const response = await fetch(path + "/api/CreateFolder", {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          setRefetchTrigger((prev) => prev + 1);
        } else {
          const errorData = await response.text();
          throw new Error(errorData);
        }
      } catch (error) {
        console.error("Error creating folder:", error);
      }
    },
    [selectedPaths, path]
  );

  // Add design to server
  const addDesignToServer = useCallback(
    async (folderName, buttonName) => {
      console.log("updatedRects in addDesignToServer:", selectedPaths);
      if (!selectedPaths || selectedPaths.length === 0) {
        setSnackbar({
          open: true,
          message: "Lütfen önce bir dizin seçin!",
          severity: "warning",
        });
        return;
      }

      try {
        let tableName = status === "0" ? "NodesTest" : "Nodes";
        console.log("Table Name:", tableName);
        console.log("Selected Paths:", selectedPaths);
        console.log("Folder Name:", folderName);
        console.log("Button Name:", buttonName);
        console.log("Updated Rects:", updatedRects);
        const response = await axios.post(path + "/api/addNode", {
          tableName: tableName,
          rects: updatedRects,
          path: "/" + selectedPaths[selectedPaths.length - 1],
          designName: folderName,
          buttonName: buttonName,
        });
        console.log("Veri başarıyla gönderildi:", response);
        console.log(
          "Backend Response:",
          selectedPaths[selectedPaths.length - 1]
        );
        console.log("Backend Yanıtı:", response);
        setSnackbar({
          open: true,
          message: "Veri başarıyla eklendi!",
          severity: "success",
        });
      } catch (error) {
        console.error("Hata Ayıklama - addDesignToServer:", {
          message: error.message,
          stack: error.stack,
          config: error.config,
          response: error.response ? error.response.data : null,
        });
        setSnackbar({
          open: true,
          message: "Bir hata oluştu. Lütfen tekrar deneyin.",
          severity: "error",
        });
      }
    },
    [updatedRects, selectedPaths, path]
  );

  // Update design to server
  const updateDesignToServer = useCallback(
    async (folderName, buttonName) => {
      processAllRows();
      console.log("childID", childID);
      try {
        let tableName = status === "0" ? "NodesTest" : "Nodes";
        const response = await axios.post(path + "/api/updateNode", {
          tableName: tableName,
          rects: updatedRects,
          path:
            selectedPaths[selectedPaths.length - 1] !== undefined
              ? selectedPaths[selectedPaths.length - 1]
              : "",
          designName: folderName,
          buttonName: buttonName,
          childID: childID,
        });

        console.log("Veri başarıyla gönderildi:", response);
        alert("Veri başarıyla Güncellendi!");
      } catch (error) {
        console.error("Veri gönderimi sırasında bir hata oluştu:", error);
        alert("Bir hata oluştu. Lütfen tekrar deneyin:.", error);
      }
    },
    [processAllRows, childID, updatedRects, selectedPaths, path]
  );

  // Submit handler
  const handleSubmit = useCallback(
  (event) => {
    event.preventDefault();
    
    const buttonClicked = event.nativeEvent.submitter.name;
    
    const folderName = event.target.elements.DesignName?.value || '';
    const buttonName = event.target.elements.ButtonName?.value || '';
    
    if (!folderName.trim()) {
      setSnackbar({
        open: true,
        message: "Lütfen dizayn adı girin!",
        severity: "warning",
      });
      return;
    }
    
    if (!buttonName.trim()) {
      setSnackbar({
        open: true,
        message: "Lütfen buton adı girin!",
        severity: "warning",
      });
      return;
    }

    if (buttonClicked === "add") {
      addDesignToServer(folderName, buttonName);
    } else if (buttonClicked === "update") {
      updateDesignToServer(folderName, buttonName);
    }
  },
  [addDesignToServer, updateDesignToServer]
);

  // Convert to PrimeReact format
  const convertToPrimeNodes = useCallback((nodes, parentPath = "") => {
  return nodes.map((node) => {
    const currentPath = parentPath ? `${parentPath}/${node.label}` : node.label;
    const hasChildren = node.children && node.children.length > 0;
    const isImage = isImageFile(node.label);
    
    return {
      key: node.id,
      label: node.label,
      selectionMode: "single",
      // İkon belirleme
      icon: hasChildren 
        ? 'pi pi-folder' 
        : isImage 
          ? 'pi pi-image' 
          : 'pi pi-folder',
      children: node.children
        ? convertToPrimeNodes(node.children, currentPath)
        : undefined,
      data: {
        path: currentPath,
        isImage: isImage,
        isPdf: isPdfFile(node.label),
        isFolder: hasChildren,
      },
      // Stil ekleme
      className: isImage ? 'image-node' : '',
      style: isImage ? {
        backgroundColor: '#e3f2fd',
        borderRadius: '4px',
        padding: '2px 4px',
      } : {},
    };
  });
}, [])

  // Handle select
  const handleSelect = useCallback(
    (e) => {
      if (treeContainerRef.current) {
        setScrollPosition(treeContainerRef.current.scrollTop);
      }
      setSelectedKeys(e.value);

      const treeData = convertToPrimeNodes(nodes);

      const getNodePath = (node, parentPath = "") => {
        const currentPath = parentPath
          ? `${parentPath}/${node.label}`
          : node.label;
        return currentPath;
      };

      const findSelectedNodes = (data, keys) => {
        let paths = [];
        const traverse = (nodes, parentPath = "") => {
          nodes.forEach((node) => {
            const currentPath = getNodePath(node, parentPath);
            if (keys[node.key]) {
              paths.push(currentPath);
            }
            if (node.children) {
              traverse(node.children, currentPath);
            }
          });
        };
        traverse(data);
        return paths;
      };

      const selectedNodePaths = findSelectedNodes(treeData, e.value);
      setSelectedPaths(selectedNodePaths);
      setImageUrl(
        imagePathRoot + "/" + selectedNodePaths[selectedNodePaths.length - 1]
      );
      console.log("Selected Paths:", selectedNodePaths);
    },
    setTimeout(() => {
      if (treeContainerRef.current) {
        treeContainerRef.current.scrollTop = scrollPosition;
      }
    }, 0)[(nodes, convertToPrimeNodes, imagePathRoot, scrollPosition)]
  );

  // Tab panel component
  function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  }

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024,
      sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // sadece .ttf ve .otf kabul
  const isAllowedFont = (f) => {
    const name = f.name?.toLowerCase() || "";
    return name.endsWith(".ttf") || name.endsWith(".otf");
  };

  // font listesi yenile (mevcut /api/fontList’i kullanır)
  const refreshFontList = useCallback(async () => {
    try {
      const response = await axios.get(path + "/api/fontList");
      setFontList(response.data);
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Font listesi yenilenemedi",
      });
    }
  }, [path]);

  const onPickFonts = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const mapped = files.map((f) => ({
      file: f,
      error: isAllowedFont(f) ? null : "Sadece .ttf ve .otf yükleyin",
    }));
    // var olanlarla birleştir, isim çakışanları tekrar ekleme
    setFontFiles((prev) => {
      const existingNames = new Set(prev.map((x) => x.file.name));
      const add = mapped.filter((m) => !existingNames.has(m.file.name));
      return [...prev, ...add];
    });
    // input’u sıfırla
    e.target.value = null;
  }, []);

  const onDropFonts = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    const mapped = files.map((f) => ({
      file: f,
      error: isAllowedFont(f) ? null : "Sadece .ttf ve .otf yükleyin",
    }));
    setFontFiles((prev) => {
      const existingNames = new Set(prev.map((x) => x.file.name));
      const add = mapped.filter((m) => !existingNames.has(m.file.name));
      return [...prev, ...add];
    });
  }, []);

  const onDragOver = useCallback((e) => e.preventDefault(), []);

  const removeFontAt = useCallback((name) => {
    setFontFiles((prev) => prev.filter((x) => x.file.name !== name));
    setFontProgress((p) => {
      const np = { ...p };
      delete np[name];
      return np;
    });
  }, []);

  const clearAllFonts = useCallback(() => {
    setFontFiles([]);
    setFontProgress({});
  }, []);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const uploadFonts = useCallback(async () => {
    if (!fontFiles.length)
      return setSnackbar({
        open: true,
        severity: "warning",
        message: "Önce font seçin",
      });
    if (fontFiles.some((f) => f.error))
      return setSnackbar({
        open: true,
        severity: "error",
        message: "Geçersiz uzantılı dosyalar var",
      });

    setFontUploading(true);
    try {
      for (const { file } of fontFiles) {
        // Base64'e çevir
        const base64Data = await convertToBase64(file);
        console.log("Uploading font:", file.name, formatBytes(file.size));
        const payload = Object.freeze({
          fileName: file.name,
          fileData: await convertToBase64(file), // data:...;base64,XXXX
          fileSize: file.size,
          fileType: file.type || "application/octet-stream",
        });

        await axios({
          method: "post",
          url: `${path}/api/UploadFont`,
          data: payload,
          headers: { "Content-Type": "application/json" },
          // optional: avoid any custom transform that might zero out data
          transformRequest: [(data) => JSON.stringify(data)],
          onUploadProgress: (pe) => {
            if (!pe.total) return;
            const percent = Math.round((pe.loaded * 100) / pe.total);
            setFontProgress((prev) => ({ ...prev, [file.name]: percent }));
          },
        });
      }

      setSnackbar({
        open: true,
        severity: "success",
        message: "Font(lar) yüklendi",
      });
      await refreshFontList();
      clearAllFonts();
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Font yükleme başarısız",
      });
    } finally {
      setFontUploading(false);
    }
  }, [fontFiles, path, refreshFontList, clearAllFonts]);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
    setTimeout(() => {
      setNotification({ open: false, message: "", severity: "success" });
    }, 3000);
  };

 const isImageFile = (filename) => {
    const imageExtensions = ['.svg', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.ico'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  const isPdfFile = (filename) => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  const isFolder = (node) => {
    return node.children && node.children.length > 0;
  };

  return (
    <div className="outer-containerSC">
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {notification.open && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            minWidth: "400px",
            backgroundColor:
              notification.severity === "success" ? "#4caf50" : "#f44336",
            color: "white",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
          }}
        >
          {notification.message}
        </div>
      )}

      <div className="containerSC">
        <div className="layout-panelSC">
          <PathContext.Provider value={[]}>
            <div>
              {shopDataRef.current.map((shop) => (
                <RecursiveAccordion
                  key={shop.name}
                  data={shop}
                  onSelect={onAccordionSelect}
                />
              ))}
            </div>
          </PathContext.Provider>
        </div>

        <div className="left-panelSC">
          <Box
            sx={{
              width: "100%",
              maxHeight: "100vh",
              overflowY: "auto",
              overflowX: "hidden",
              "@media (max-width: 1200px)": {
                maxHeight: "90vh",
              },
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={value}
                onChange={handleChanges}
                aria-label="basic tabs example"
              >
                <Tab label="Dizayn Tanımlama" {...a11yProps(0)} />
                <Tab label="Dosya Tanımlama" {...a11yProps(1)} />
                <Tab label="Font Yükleme" {...a11yProps(2)} />
                 {user?.DesignAdmin === 1 && (<Tab label="Kullanıcı Yetkilendirme" {...a11yProps(2)} />  )}
              </Tabs>
            </Box>

            <TabPanel value={value} index={0}>
              <form
                onSubmit={handleSubmit}
                style={{
                  maxWidth: "1400px",
                  margin: "0 auto",
                  padding: "20px",
                }}
              >
                {/* Modern header */}
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #59a3aaff 0%, #344b7bff 100%)",
                    padding: "30px",
                    borderRadius: "16px",
                    marginBottom: "30px",
                    boxShadow: "0 8px 32px rgba(102, 126, 234, 0.2)",
                  }}
                >
                  <h2
                    style={{
                      color: "white",
                      margin: 0,
                      fontSize: "28px",
                      fontWeight: "700",
                    }}
                  >
                    Tanımlama Paneli
                  </h2>
                </div>

                {/* Modern path display */}
                {selectedPaths.length > 0 && (
                  <div
                    style={{
                      background: "linear-gradient(to right, #f8f9fa, #e9ecef)",
                      padding: "15px 20px",
                      borderRadius: "12px",
                      marginBottom: "20px",
                      borderLeft: "4px solid #667eea",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#6c757d",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Seçili Yol
                    </span>
                    <h3
                      style={{
                        color: "#495057",
                        margin: "5px 0 0 0",
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      {selectedPaths[selectedPaths.length - 1]}
                    </h3>
                  </div>
                )}

                {/* Grid Layout */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "30px",
                    marginBottom: "30px",
                  }}
                >
                  {/* Sol taraf - Tree */}
                  <div
                    style={{
                      background: "white",
                      padding: "25px",
                      borderRadius: "16px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    }}
                  >
                    <h3
                      style={{
                        color: "#667eea",
                        marginTop: 0,
                        fontSize: "18px",
                        fontWeight: "600",
                        marginBottom: "20px",
                      }}
                    >
                      Dosya Ağacı
                    </h3>

                    <Box
                      ref={treeContainerRef}
                      sx={{
                        height: "450px",
                        width: "100%",
                        overflow: "auto",
                        borderRadius: "12px",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <Tree
                        value={convertToPrimeNodes(nodes)}
                        expandedKeys={expandedKeys}
                        onToggle={(e) => setExpandedKeys(e.value)}
                        selectionMode="checkbox"
                        selectionKeys={selectedKeys}
                        onSelectionChange={handleSelect}
                        filter
                        filterMode="lenient"
                        propagateSelectionDown={false}
                        style={{
                          backgroundColor: "#f8f9fa",
                          padding: "10px",
                        }}
                      />
                    </Box>
                  </div>

                  {/* Sağ taraf - Form fields */}
                  <div
                    style={{
                      background: "white",
                      padding: "25px",
                      borderRadius: "16px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    }}
                  >
                    <h3
                      style={{
                        color: "#764ba2",
                        marginTop: 0,
                        fontSize: "18px",
                        fontWeight: "600",
                        marginBottom: "20px",
                      }}
                    >
                      Dizayn Bilgileri
                    </h3>

                    <TextField
                      id="DesignName"
                      name="DesignName"
                      label="Dizayn Adı"
                      variant="outlined"
                      fullWidth
                      style={{ marginBottom: "20px" }}
                    />
                    <TextField
                      id="ButtonName"
                      name="ButtonName"
                      label="Buton İsmi"
                      variant="outlined"
                      fullWidth
                    />
                  </div>
                </div>

                {/* Modern Card Layout */}
                <div
                  style={{
                    background: "white",
                    padding: "25px",
                    borderRadius: "16px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    marginBottom: "30px",
                  }}
                >
                  <h3
                    style={{
                      color: "#667eea",
                      marginTop: 0,
                      fontSize: "18px",
                      fontWeight: "600",
                      marginBottom: "20px",
                    }}
                  >
                    Parametre Kartları ({[...filledRows, ...rowsAdd].length}/4)
                  </h3>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {[
                      ...new Map(
                        [...filledRows, ...rowsAdd].map((row) => [row.id, row])
                      ).values(),
                    ]
                      .slice(0, 4)
                      .map((row, index) => (
                        <Paper
                          key={row.id}
                          elevation={selectedRows.includes(row.id) ? 8 : 2}
                          sx={{
                            p: 2.5,
                            border: selectedRows.includes(row.id)
                              ? "2px solid #667eea"
                              : "1px solid #e9ecef",
                            borderRadius: "12px",
                            cursor: "pointer",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            background: selectedRows.includes(row.id)
                              ? "linear-gradient(to right, #f8f9ff, #ffffff)"
                              : "white",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 8px 24px rgba(102, 126, 234, 0.15)",
                            },
                          }}
                          onClick={() => {
                            setSelectedRows([row.id]);
                            setCheckedRows({ [row.id]: true });
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Checkbox
                              checked={selectedRows.includes(row.id)}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setSelectedRows(isChecked ? [row.id] : []);
                                setCheckedRows(
                                  isChecked ? { [row.id]: true } : {}
                                );
                              }}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                color: "#59a3aaff",
                                "&.Mui-checked": { color: "#667eea" },
                              }}
                            />
                            <Typography
                              variant="h6"
                              sx={{
                                flexGrow: 1,
                                ml: 1,
                                fontSize: "16px",
                                fontWeight: "600",
                                color: selectedRows.includes(row.id)
                                  ? "#344b7bff"
                                  : "#495057",
                              }}
                            >
                              Kart {index + 1}
                            </Typography>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRow(row.id);
                              }}
                              size="small"
                              sx={{
                                color: "#dc3545",
                                "&:hover": {
                                  background: "rgba(220, 53, 69, 0.1)",
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>

                                                  <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fit, minmax(150px, 1fr))",
                              gap: 2,
                            }}
                          >
                            <TextField
                              label="Height"
                              type="number"
                              size="small"
                              defaultValue={row.Height || ""}
                              onBlur={(e) => {
                                const newRow = {
                                  ...row,
                                  Height: Number(e.target.value),
                                };
                                if (filledRows.some((r) => r.id === row.id)) {
                                  setFilledRows((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                } else {
                                  setRowsAdd((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <TextField
                              label="Width"
                              type="number"
                              size="small"
                              defaultValue={row.Width || ""}
                              onBlur={(e) => {
                                const newRow = {
                                  ...row,
                                  Width: Number(e.target.value),
                                };
                                if (filledRows.some((r) => r.id === row.id)) {
                                  setFilledRows((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                } else {
                                  setRowsAdd((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <TextField
                              label="X"
                              type="number"
                              size="small"
                              defaultValue={row.X || ""}
                              onBlur={(e) => {
                                const newRow = {
                                  ...row,
                                  X: Number(e.target.value),
                                };
                                if (filledRows.some((r) => r.id === row.id)) {
                                  setFilledRows((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                } else {
                                  setRowsAdd((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <TextField
                              label="Y"
                              type="number"
                              size="small"
                              defaultValue={row.Y || ""}
                              onBlur={(e) => {
                                const newRow = {
                                  ...row,
                                  Y: Number(e.target.value),
                                };
                                if (filledRows.some((r) => r.id === row.id)) {
                                  setFilledRows((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                } else {
                                  setRowsAdd((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <TextField
                              label="Text Height"
                              type="number"
                              size="small"
                              defaultValue={row.TextHeight || ""}
                              onBlur={(e) => {
                                const newRow = {
                                  ...row,
                                  TextHeight: Number(e.target.value),
                                };
                                if (filledRows.some((r) => r.id === row.id)) {
                                  setFilledRows((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                } else {
                                  setRowsAdd((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <FormControl
                              size="small"
                              fullWidth
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InputLabel>Font</InputLabel>
                              <Select
                                value={row.Font || ""}
                                label="Font"
                                onChange={(e) => {
                                  const newRow = {
                                    ...row,
                                    Font: e.target.value,
                                  };
                                  if (filledRows.some((r) => r.id === row.id)) {
                                    setFilledRows((prevRows) =>
                                      prevRows.map((r) =>
                                        r.id === row.id ? newRow : r
                                      )
                                    );
                                  } else {
                                    setRowsAdd((prevRows) =>
                                      prevRows.map((r) =>
                                        r.id === row.id ? newRow : r
                                      )
                                    );
                                  }
                                }}
                              >
                                {fontList.map((font) => (
                                  <MenuItem key={font.name} value={font.name}>
                                    {font.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              label="Rotation"
                              type="number"
                              size="small"
                              defaultValue={row.Rotation || ""}
                              onBlur={(e) => {
                                const newRow = {
                                  ...row,
                                  Rotation: Number(e.target.value),
                                };
                                if (filledRows.some((r) => r.id === row.id)) {
                                  setFilledRows((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                } else {
                                  setRowsAdd((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <TextField
                              label="Curve"
                              type="number"
                              size="small"
                             
                              defaultValue={row.Curve || ""}
                              onBlur={(e) => {
                                const newRow = {
                                  ...row,
                                  Curve: Number(e.target.value),
                                };
                                if (filledRows.some((r) => r.id === row.id)) {
                                  setFilledRows((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                } else {
                                  setRowsAdd((prevRows) =>
                                    prevRows.map((r) =>
                                      r.id === row.id ? newRow : r
                                    )
                                  );
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Box>

                          <TextField
                            label="Deneme Text"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                             style={{ marginTop: "8px" }}
                            defaultValue={row.Text || ""}
                            onBlur={(e) => {
                              const newRow = { ...row, Text: e.target.value };
                              if (filledRows.some((r) => r.id === row.id)) {
                                setFilledRows((prevRows) =>
                                  prevRows.map((r) =>
                                    r.id === row.id ? newRow : r
                                  )
                                );
                              } else {
                                setRowsAdd((prevRows) =>
                                  prevRows.map((r) =>
                                    r.id === row.id ? newRow : r
                                  )
                                );
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Paper>
                      ))}
                  </Box>

                  {[...filledRows, ...rowsAdd].length < 4 && (
                    <Button
                      variant="contained"
                      onClick={addEmptyRow}
                      fullWidth
                      sx={{
                        mt: 2,
                        py: 1.5,
                        borderRadius: "12px",
                        background:
                         "linear-gradient(135deg, #59a3aaff 0%, #344b7bff 100%)",
                        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                        fontWeight: "600",
                        fontSize: "15px",
                        textTransform: "none",
                        "&:hover": {
                          background:
                           "linear-gradient(135deg, #59a3aaff 0%, #344b7bff 100%)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      + Yeni Kart Ekle ({[...filledRows, ...rowsAdd].length}/4)
                    </Button>
                  )}
                </div>

                {/* Modern Action Buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: "15px",
                    justifyContent: "center",
                    marginTop: "30px",
                  }}
                >
                  <button
                    type="submit"
                    name="add"
                    style={{
                      padding: "12px 32px",
                      borderRadius: "12px",
                      border: "none",
                      background:
                        "linear-gradient(45deg, #28a745 30%, #20c997 90%)",
                      color: "white",
                      fontWeight: "600",
                      fontSize: "15px",
                      cursor: "pointer",
                      boxShadow: "0 4px 15px rgba(40, 167, 69, 0.3)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 6px 20px rgba(40, 167, 69, 0.4)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 15px rgba(40, 167, 69, 0.3)";
                    }}
                  >
                    💾 Ekle
                  </button>

                  <button
                    type="submit"
                    name="update"
                    style={{
                      padding: "12px 32px",
                      borderRadius: "12px",
                      border: "none",
                      background:
                        "linear-gradient(45deg, #ffc107 30%, #ff9800 90%)",
                      color: "white",
                      fontWeight: "600",
                      fontSize: "15px",
                      cursor: "pointer",
                      boxShadow: "0 4px 15px rgba(255, 193, 7, 0.3)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 6px 20px rgba(255, 193, 7, 0.4)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 15px rgba(255, 193, 7, 0.3)";
                    }}
                  >
                    🔄 Güncelle
                  </button>

                  <button
                    type="button"
                    onClick={processAllRows}
                    style={{
                      padding: "12px 32px",
                      borderRadius: "12px",
                      border: "none",
                      background:
                        "linear-gradient(45deg, #007bff 30%, #0056b3 90%)",
                      color: "white",
                      fontWeight: "600",
                      fontSize: "15px",
                      cursor: "pointer",
                      boxShadow: "0 4px 15px rgba(0, 123, 255, 0.3)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 6px 20px rgba(0, 123, 255, 0.4)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 15px rgba(0, 123, 255, 0.3)";
                    }}
                  >
                    ▶️ Dene
                  </button>
                </div>
              </form>
            </TabPanel>

            <TabPanel value={value} index={1}>
              <FolderManagementPanel
                value={value}
                nodes={nodes}
                expandedKeys={expandedKeys}
                setExpandedKeys={setExpandedKeys}
                selectedKeys={selectedKeys}
                handleSelect={handleSelect}
                selectedPaths={selectedPaths}
                uploadedFile={uploadedFile}
                handleFileChange={handleFileChange}
                handleRemoveFile={handleRemoveFile}
                handleFileUpload={handleFileUpload}
                path={path}
                setRefetchTrigger={setRefetchTrigger}
                convertToPrimeNodes={convertToPrimeNodes}
                showNotification={showNotification}
              />
            </TabPanel>

            <TabPanel value={value} index={2}>
              <Box
                sx={{
                  width: "95%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography variant="h6">Font Yükleme</Typography>
                <Typography variant="body2" color="text.secondary">
                  Yalnızca <b>.ttf</b> ve <b>.otf</b> dosyaları desteklenir.
                  Birden fazla dosyayı aynı anda yükleyebilirsiniz.
                </Typography>

                {/* Drag & Drop Alanı */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    textAlign: "center",
                    borderStyle: "dashed",
                    background: "#fcfcfc",
                    borderRadius: 2,
                  }}
                  onDragOver={onDragOver}
                  onDrop={onDropFonts}
                >
                  Dosyaları buraya sürükleyip bırakın
                  <br />
                  <Button
                    component="label"
                    variant="contained"
                    sx={{ mt: 2 }}
                    startIcon={<CloudUploadIcon />}
                    disabled={fontUploading}
                  >
                    Dosya Seç
                    <input
                      type="file"
                      hidden
                      multiple
                      accept=".ttf,.otf"
                      onChange={onPickFonts}
                    />
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    (Desteklenen: .ttf, .otf)
                  </Typography>
                </Paper>

                {/* Seçilen dosyalar listesi */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      gap: 1,
                    }}
                  >
                    <Chip label={`Seçilen: ${fontFiles.length}`} size="small" />
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      onClick={clearAllFonts}
                      disabled={!fontFiles.length || fontUploading}
                    >
                      Hepsini Temizle
                    </Button>
                  </Box>

                  {fontFiles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Henüz dosya seçilmedi.
                    </Typography>
                  ) : (
                    <List
                      dense
                      sx={{ width: "100%", maxHeight: 280, overflow: "auto" }}
                    >
                      {fontFiles.map(({ file, error }) => (
                        <ListItem
                          key={file.name}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => removeFontAt(file.name)}
                              disabled={fontUploading}
                              size="small"
                            >
                              <ClearIcon />
                            </IconButton>
                          }
                          sx={{ alignItems: "flex-start" }}
                        >
                          <PhotoIcon
                            fontSize="small"
                            style={{ marginRight: 8, marginTop: 2 }}
                          />
                          <ListItemText
                            primary={
                              <span>
                                {file.name}{" "}
                                {error && (
                                  <Typography
                                    component="span"
                                    color="error"
                                    sx={{ ml: 1 }}
                                  >
                                    ({error})
                                  </Typography>
                                )}
                              </span>
                            }
                            secondary={
                              <div>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatBytes(file.size)}
                                </Typography>
                                {fontUploading && (
                                  <LinearProgress
                                    variant="determinate"
                                    value={fontProgress[file.name] ?? 0}
                                    sx={{ mt: 1, height: 6, borderRadius: 1 }}
                                  />
                                )}
                              </div>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>

                {/* Aksiyonlar */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={uploadFonts}
                    disabled={
                      !fontFiles.length ||
                      fontFiles.some((f) => f.error) ||
                      fontUploading
                    }
                  >
                    Yükle
                  </Button>
                  {fontUploading && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LinearProgress sx={{ width: 180 }} />
                      <Typography variant="caption" color="text.secondary">
                        Yükleniyor…
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </TabPanel>
              <TabPanel value={value} index={3}>
              <Box
                sx={{
                  width: "95%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
              <ShopAuthManagement
                   path={path}
                />
              </Box>
            </TabPanel>
          </Box>
        </div>

        <div className="right-panelSC">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
              width: "100%",
            }}
          >
            {/* Alert Messages Section */}
            <div style={{ width: "100%", marginTop: 30 }}>
              {successMessage && (
                <Alert severity="success" onClose={() => setSuccessMessage("")}>
                  {successMessage}
                </Alert>
              )}
              {errorMessage && (
                <Alert severity="error" onClose={() => setErrorMessage("")}>
                  {errorMessage}
                </Alert>
              )}
            </div>

            {/* SVG Content Section - Memoized Component */}
            <div
              style={{
                width: "100%",
                minHeight: "600px", // Minimum yükseklik ekledik
                display: "flex",
                justifyContent: "center",
                flex: 1, // Flex ile büyümesini sağladık
              }}
            >
              <SvgPreview svgContent={svgContent} svgExample={svgExample} />
            </div>

            {/* Navigation Buttons Section */}
            <div style={{ width: "100%" }}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                style={{ marginBottom: "20px" }}
              >
                <Button
                  id={"up"}
                  onClick={handleButtonClick}
                  variant="outlined"
                  startIcon={<ArrowUpwardIcon />}
                  style={{ marginBottom: "10px" }}
                />
                <Box display="flex">
                  <Button
                    id={"left"}
                    onClick={handleButtonClick}
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    style={{ marginRight: "10px" }}
                  />
                  <Button
                    id={"right"}
                    onClick={handleButtonClick}
                    variant="outlined"
                    startIcon={<ArrowForwardIcon />}
                    style={{ marginLeft: "10px" }}
                  />
                </Box>
                <Button
                  id={"down"}
                  onClick={handleButtonClick}
                  variant="outlined"
                  startIcon={<ArrowDownwardIcon />}
                  style={{ marginTop: "10px" }}
                />
              </Box>
            </div>
          </div>
        </div>
      </div>

      <div>
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Download SVG</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please enter a name for the file.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="File Name"
              type="text"
              fullWidth
              variant="outlined"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={downloadSVG} variant="contained" color="primary">
              Download
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default SvgAdminPanel;
